import { computed, inject, Injectable } from '@angular/core';
import { Observable, switchMap, tap, throwError } from 'rxjs';
import { RouteNavigator } from './route-navigator.service';
import { AuthStore, PendingLoginExpiredError, PendingLoginStore } from '../stores';
import { GozemMoneyAccountStore } from '../stores/gozem-money-account.store';
import { AccountLogoStore } from '../stores/account-logo.store';
import { AuthApi } from '../apis';
import {
  Storage,
  MerchantService,
  AccountService,
  TokenRefreshService,
  SentryService,
} from '../services';
import { isLoginSuccess } from '../models';
import type {
  AccountSessionGateway,
  LoginCredentials,
  LoginResponse,
  LoginSuccessResponse,
  MerchantAccount,
  User,
} from '../models';

/**
 * The only credential gateway the web app supports.
 *
 * `/account/session` is shared with the mobile OTP flow, so the field is required and
 * enumerated (`sms | email | password | whatsapp`). Web is password-only, and this constant
 * is the single line to revisit if that ever changes.
 */
const WEB_LOGIN_GATEWAY: AccountSessionGateway = 'password';

/**
 * AuthService — orchestrates the two-phase merchant login.
 *
 * ```
 * verify-merchant-login ──► /auth/login ──┬── login_successful: true  ──► session
 *                                          └── login_successful: false ──► PendingLoginStore
 *                                                                            │
 *                                          /account/session ◄── user picks ──┘
 *                                                   └──► session
 * ```
 *
 * `establishSession` is the **only** thing in the app allowed to install a session. Both
 * branches funnel through it, so there is exactly one place where `AuthStore`, `Storage`,
 * `AccountService` and Sentry are written — previously this logic existed in three slightly
 * different copies and they drifted.
 *
 * Security:
 * - Access token in memory (`AuthStore`).
 * - Refresh token in localStorage (`Storage`).
 * - The in-flight credential in memory only (`PendingLoginStore`), TTL-bounded.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly authStore = inject(AuthStore);
  private readonly pendingLogin = inject(PendingLoginStore);
  private readonly gmAccountStore = inject(GozemMoneyAccountStore);
  private readonly accountLogoStore = inject(AccountLogoStore);
  private readonly authApi = inject(AuthApi);
  private readonly storage = inject(Storage);
  private readonly routeNavigator = inject(RouteNavigator);
  private readonly merchantService = inject(MerchantService);
  private readonly accountService = inject(AccountService);
  private readonly tokenRefresh = inject(TokenRefreshService);
  private readonly sentryService = inject(SentryService);

  // Expose auth state from store
  readonly currentUser = computed(() => this.authStore.currentUser());
  readonly currentCountryDetail = computed(() => this.authStore.currentCountryDetail());
  readonly isAuthenticated = computed(() => this.authStore.isAuthenticated());

  // ── Phase 1 ────────────────────────────────────────────────────────────────

  /**
   * Verify the username, then check the password against the primary account.
   *
   * Resolves with a union. On the success branch the session is already installed by the
   * time subscribers run. On the selection branch **no session exists** — the credential
   * and account list are parked in `PendingLoginStore` and the caller must route to the
   * picker.
   *
   * Deliberately does not treat `login_successful: false` as an error: it is a 2xx and a
   * normal outcome, and modelling it as a failure is what produced the original crash.
   */
  loginWithVerification(credentials: LoginCredentials): Observable<LoginResponse> {
    return this.authApi.verifyMerchantLogin(credentials.email).pipe(
      switchMap((verifyResponse) => this.authApi.login(credentials, verifyResponse.account_id)),
      tap((response) => {
        if (isLoginSuccess(response)) {
          this.establishSession(response);
          return;
        }

        this.pendingLogin.start({
          username: credentials.email,
          credential: credentials.password,
          accounts: response.accounts,
        });
      }),
    );
  }

  // ── Phase 2 ────────────────────────────────────────────────────────────────

  /**
   * Selection mode — open a session against the merchant the user picked.
   *
   * Replays the parked credential against `/account/session`. Fails fast with
   * `PendingLoginExpiredError` if the pending login has aged out, so a stale click never
   * reaches the network.
   */
  completeAccountSelection(accountId: string): Observable<LoginSuccessResponse> {
    const pending = this.pendingLogin.snapshot();

    if (!pending) {
      return throwError(() => new PendingLoginExpiredError());
    }

    return this.authApi
      .enterAccountSession({
        accountId,
        credential: pending.credential,
        gateway: WEB_LOGIN_GATEWAY,
      })
      .pipe(
        tap((response) => {
          this.establishSession(response, accountId);
          // Only once the session is safely installed — a failed call must leave the
          // credential in place so the user can retry without re-typing it.
          this.pendingLogin.clear();
        }),
      );
  }

  /**
   * Confirmation mode — the session already exists (single-merchant login), so choosing the
   * account is just recording the selection. No network call, no re-authentication.
   */
  confirmActiveAccount(accountId: string): void {
    this.accountService.setActiveAccount(accountId);
  }

  /**
   * Switch the active account *from inside the app*, the correct way (resolves decision D4).
   *
   * A merchant switch is a scope change the backend can only grant with a fresh, account-scoped
   * session — there is no token-exchange endpoint (confirmed against the Android app, which
   * performs a full re-login on switch). So this replays the user's re-typed password against
   * `/account/session` for the target account and, on success, funnels the payload through
   * `establishSession` — the single session writer. That overwrites `token`, `merchant_id`,
   * `merchant_user_id`, the accounts list, persisted `userData`, and the active account *as one
   * consistent set*, which is exactly what the old optimistic `AccountService.switchAccount`
   * failed to do: it moved only the active `accountId`, leaving every Gozem Money request to
   * carry the new `account_id` under the previous merchant's `merchant_id`/token — the mismatch
   * the backend rejects with error 1032 ("Compte Gozem Money introuvable").
   *
   * Unlike `completeAccountSelection` this does not depend on `PendingLoginStore`: the password
   * comes straight from the switch dialog and is never parked. The caller performs a full reset
   * (reload to /wallet) afterwards so every page-scoped store rebuilds against the new session;
   * nothing here clears in-memory stores because the reload discards them anyway.
   *
   * @param accountId - the `MerchantAccount.accountId` the user picked in the switcher
   * @param password  - the password re-typed in the switch dialog (never persisted)
   */
  switchAccount(accountId: string, password: string): Observable<LoginSuccessResponse> {
    return this.authApi
      .enterAccountSession({
        accountId,
        credential: password,
        gateway: WEB_LOGIN_GATEWAY,
      })
      .pipe(tap((response) => this.establishSession(response, accountId)));
  }

  /** Accounts to show in the picker while a login is pending selection. */
  readonly pendingAccounts = computed<MerchantAccount[]>(() => this.pendingLogin.accounts());

  /** True when a credential is parked awaiting merchant selection and still valid. */
  hasFreshPendingLogin(): boolean {
    return this.pendingLogin.isFresh();
  }

  /** True when a login was started but timed out before a merchant was chosen. */
  hasExpiredPendingLogin(): boolean {
    return this.pendingLogin.isExpired();
  }

  /** Discard a pending login — leaving the picker, or after it expired. */
  clearPendingLogin(): void {
    this.pendingLogin.clear();
  }

  // ── Session lifecycle ──────────────────────────────────────────────────────

  /**
   * Install a session. The single writer.
   *
   * @param response - a successful `/auth/login` or `/account/session` payload
   * @param selectedAccountId - the account the user chose, when there was a choice
   */
  private establishSession(response: LoginSuccessResponse, selectedAccountId?: string): void {
    // Accounts first: the user's role and the active selection are read back out of the
    // store below, so it has to be populated before anything else looks at it.
    this.accountService.initializeAccounts(response.accounts, selectedAccountId);

    const user = this.mapToUser(response);

    this.authStore.setSession({
      accessToken: response.token_data.access_token,
      expiresInSeconds: response.token_data.expires_in,
      user,
      countryDetail: response.country_detail,
      token: response.token,
      merchantId: response.merchant_id,
      merchantUserId: response.merchant_user_id,
    });

    this.storage.setRefreshToken(
      response.token_data.refresh_token,
      response.token_data.refresh_expires_in,
    );

    // Raw rows are persisted, never normalized ones: `normalizeMerchantAccounts` maps
    // snake_case in to camelCase out, so feeding it its own output would drop every row.
    this.storage.setUserData(
      user,
      response.country_detail,
      response.accounts,
      response.token,
      response.merchant_id,
      response.merchant_user_id,
    );

    this.sentryService.setUser(user, response.merchant_id);
  }

  /**
   * Map a session payload to the `User` model.
   *
   * `role` comes from the active account rather than `accounts[0]`: for a multi-merchant
   * user the first row is not necessarily the one they signed into.
   */
  private mapToUser(response: LoginSuccessResponse): User {
    return {
      id: response.merchant_user_id,
      email: response.email,
      name: response.merchant_user_name || response.first_name,
      role: this.accountService.getActiveAccount()?.role || 'user',
      merchantId: response.merchant_id,
      merchantName: response.merchant_name,
      firstName: response.first_name,
      phone: response.phone,
      picture: response.picture,
      isApproved: response.is_approved,
    };
  }

  /**
   * Logout user.
   *
   * Clears memory session, the parked credential and localStorage, then navigates to login.
   */
  logout(): void {
    this.merchantService.clearMerchantData();
    this.accountService.clearAccounts();

    // In-memory GozemMoney wallet account id, never persisted
    this.gmAccountStore.clear();

    // Cached per-account merchant logos (memory + localStorage) — one user must never
    // see another's logos.
    this.accountLogoStore.clear();

    this.pendingLogin.clear();
    this.authStore.clearSession();
    this.storage.clearRefreshToken();
    this.storage.clearUserData();

    // Reset refresh state so a stale isRefreshing=true cannot block the next login session
    this.tokenRefresh.reset();

    this.sentryService.clearUser();

    this.routeNavigator.to(['/login']);
  }

  /**
   * Get current access token (for manual use)
   */
  getToken(): string | null {
    return this.authStore.getAccessToken();
  }

  /**
   * Forgot password
   */
  forgotPassword(email: string): Observable<{ success: boolean; message?: string }> {
    return this.authApi.forgotPassword(email);
  }
}
